import { organisationApi } from "@/api/organisation.api";
import { ActivableParticipationStats, ActivatedParticipationsStats, EvaluatedParticipationsCountStats, LastEvaluatedParticipationStats, Organisation, OrganisationStatType, ParticipationsStateStats } from "@/types/company";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { useUser } from "./user.query";
import { queryClient } from "./queryClient";
import { storeToRefs } from "pinia";
import { membersApi } from "@/api/members.api";
import { roleApi } from "@/api/role.api";

export async function fetchOrganisationQuery(organisationId: string) {
  return await queryClient.fetchQuery({
    queryKey: ['organisation', organisationId],
    queryFn: async () => {
      return await organisationApi.get(organisationId);
    }
  });
}

export async function fetchOrganisationsQuery() {
  return await queryClient.fetchQuery({
    queryKey: ['organisation'],
    queryFn: async () => {
      const allOrganisations =  await organisationApi.getAll();
      allOrganisations.forEach((org) => {
        queryClient.setQueryData(['organisations', org.id], org);
      });
      return allOrganisations;
    }
  });
}

export function useOrganisations() {
  const queryClient = useQueryClient();
  const { data: user, isPending } = useUser();

  const enabled = computed(() => {
    return !isPending.value;
  });

  return useQuery({
    queryKey: ['organisations'],
    queryFn: async () => {
      const allOrganisations = await organisationApi.getAll();
      allOrganisations.forEach((org) => {
        queryClient.setQueryData(['organisations', org.id], org);
      });
      return allOrganisations;
    },
    enabled: enabled,
  });
}

export function useOrganisation() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['organisations', appStore.currentOrganisationId],
    queryFn: () => organisationApi.get(toValue(appStore.currentOrganisationId)),
    enabled: !!toValue(appStore.currentOrganisationId)
  });
}

export function useCreateOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string }) => organisationApi.create(data.name),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['organisations', data.id], data);
    }
  });
}

export function useEditOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Organisation) => organisationApi.edit(data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organisations', variables.id] });
    }
  });
}

export function useOrganisationCredits() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['organisations', appStore.currentOrganisationId, 'credits'],
    queryFn: () => organisationApi.getCredits(toValue(appStore.currentOrganisationId)),
    enabled: !!toValue(appStore.currentOrganisationId)
  });
}

export function useAddOrganisationCredits() {
  return useMutation({
    mutationFn: (data: { organisationId: string, credits: number }) => organisationApi.addCredits(data.organisationId, data.credits),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organisations', variables.organisationId, 'credits'] });
    }
  });
}

export function useAllOrgnisationRoles() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['organisations', appStore.currentOrganisationId, 'roles'],
    queryFn: () => roleApi.getAllAvailableRoles(toValue(appStore.currentOrganisationId)),
    enabled: !!toValue(appStore.currentOrganisationId)
  });
}

// Transactions

export function useOrganisationTransactions() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['organisations', appStore.currentOrganisationId, 'transactions'],
    queryFn: () => organisationApi.getAllTransactions(toValue(appStore.currentOrganisationId)),
    enabled: !!toValue(appStore.currentOrganisationId),
  });
}

export function useOrganisationTransaction(transactionId: Ref<string>) {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['organisations', appStore.currentOrganisationId, 'transactions', transactionId],
    queryFn: () => organisationApi.getTransaction(toValue(appStore.currentOrganisationId), toValue(transactionId)),
    refetchInterval: (query) => {
      if (query.state.dataUpdateCount >= 10) return false;
      return 1000;
    },
    enabled: (!!toValue(appStore.currentOrganisationId) && !!toValue(transactionId)),
  });
}

// Stats

export function useActivableParticipationsStats() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['organisations', appStore.currentOrganisationId, 'stats', OrganisationStatType.ACTIVABLE_PARTICIPATIONS],
    queryFn: async () => {
      const r = await organisationApi.getStats(toValue(appStore.currentOrganisationId), OrganisationStatType.ACTIVABLE_PARTICIPATIONS) as { entries: ActivableParticipationStats[] };
      return r.entries;
    },
    enabled: !!toValue(appStore.currentOrganisationId)
  });
}

export function useActivatedParticipationsStats() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['organisations', appStore.currentOrganisationId, 'stats', OrganisationStatType.ACTIVATED_PARTICIPATIONS],
    queryFn: () => organisationApi.getStats(toValue(appStore.currentOrganisationId), OrganisationStatType.ACTIVATED_PARTICIPATIONS) as Promise<ActivatedParticipationsStats>,
    enabled: !!toValue(appStore.currentOrganisationId)
  });
}

export function useEvaluationCountStats() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['organisations', appStore.currentOrganisationId, 'stats', OrganisationStatType.EVALUATED_PARTICIPATIONS_COUNT],
    queryFn: () => organisationApi.getStats(toValue(appStore.currentOrganisationId), OrganisationStatType.EVALUATED_PARTICIPATIONS_COUNT) as Promise<EvaluatedParticipationsCountStats>,
    enabled: !!toValue(appStore.currentOrganisationId)
  });
}

export function useLastEvaluationsStats() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['organisations', appStore.currentOrganisationId, 'stats', OrganisationStatType.LAST_EVALUATED_PARTICIPATIONS],
    queryFn: async () => {
      const r: { entries: LastEvaluatedParticipationStats[] } = await organisationApi.getStats(toValue(appStore.currentOrganisationId), OrganisationStatType.LAST_EVALUATED_PARTICIPATIONS) as { entries: LastEvaluatedParticipationStats[] };
      return r.entries;
    },
    enabled: !!toValue(appStore.currentOrganisationId)
  });
}

export function useParticipationsStateStats() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['organisations', appStore.currentOrganisationId, 'stats', OrganisationStatType.PARTICIPATIONS_STATE],
    queryFn: () => organisationApi.getStats(toValue(appStore.currentOrganisationId), OrganisationStatType.PARTICIPATIONS_STATE) as Promise<ParticipationsStateStats>,
    enabled: !!toValue(appStore.currentOrganisationId)
  });
}